import { HvHostMap, PostHv, Put } from '@/connection/helpers';

export async function UploadFileToProvider(
  endpoint: {
    url: string;
    fields: { [key: string]: string };
    cdnHost?: string;
  },
  file: File | Blob,
  progress?: (uploadedBytes: number) => void,
  filename?: string,
  returnFileHttpRequest?: (request: XMLHttpRequest) => unknown
): Promise<string> {
  const { url, fields: data, cdnHost } = endpoint;
  const formData = new FormData();

  // AWS requires extra signing params in the form
  if (data) {
    for (const key in data) {
      if (key !== 'key') {
        formData.append(key, data[key]);
      }
    }
  }

  formData.append('file', file, filename);

  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();

    returnFileHttpRequest?.(request);
    request.open('POST', url);

    request.upload.onprogress = (e: ProgressEvent<EventTarget>) => {
      const uploaded = e.loaded;
      progress?.(uploaded);
    };

    request.onerror = (e) => {
      console.error('UploadFileToProvider error', e);
      reject(e);
    };

    request.onload = () => {
      if (cdnHost) {
        resolve(`${cdnHost}/${data?.Key}`);
      }
      resolve(`${url}/${data?.Key}`);
    };

    request.send(formData);
  });
}

type GetUploadEndpointProps = {
  filename: string;
  isThumb?: boolean;
  isLiveSession?: boolean;
  contentType?: string;
  headers?: Record<string, string>;
};

export const GetUploadEndpoint = async ({
  filename,
  isThumb = false,
  isLiveSession = false,
  contentType = '',
  headers = {},
}: GetUploadEndpointProps): Promise<unknown> => {
  if (isLiveSession) {
    return PostHv({
      host: HvHostMap.registration,
      path: '/upload-request',
      body: {
        filename,
        isThumb,
        contentType,
      },
    });
  } else {
    return Put({
      path: '/v3/admin/channel/upload-endpoint',
      headers,
      body: { filename, isThumb, contentType },
    });
  }
};

type SetItemContentTypeProps = {
  filename: string;
  contentType: string;
  isLiveSession?: boolean;
  headers?: Record<string, string>;
};

export const SetItemContentType = async ({
  filename,
  contentType,
  isLiveSession = false,
  headers = {},
}: SetItemContentTypeProps) => {
  if (isLiveSession) {
    let _filename = filename;
    if (filename.startsWith('http')) {
      const uri = new URL(filename);
      _filename = uri.pathname.substring(1); // Get path without leading /
    }

    return PostHv({
      host: HvHostMap.registration,
      path: '/set-content-type',
      body: {
        filename: _filename,
        contentType,
      },
    });
  }

  if (filename.startsWith('http')) {
    const uri = new URL(filename);
    filename = uri.pathname.substring(1); // Get path without leading /
  }

  return Put({
    path: '/v3/admin/channel/upload-endpoint/content-type',
    headers,
    body: { filename, contentType },
  });
};

export function getUploadFilename(filename: string): string {
  return filename.replace(/[^0-9a-zA-Z.]/g, '-');
}

export function convertByteToHex(byte: number): string {
  return ('0' + byte.toString(16))?.slice(-2);
}

export async function getFileFirstMBAsHex(file: File): Promise<string[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (!result || typeof result === 'string') return;

      const bytes: string[] = [];
      const uint = new Uint8Array(result);
      uint.forEach((byte) => {
        const byteAsHex = convertByteToHex(byte);
        if (byteAsHex) bytes.push(byteAsHex);
      });

      resolve(bytes);
    };

    // read first 50KB of file to not hit ArrayBuffer limit of browser
    reader.readAsArrayBuffer(file.slice(0, 50000));
    reader.onerror = (error) => reject(error);
  });
}

export function extractFileType(fileName: string): string {
  const sections = fileName.split('.');
  return sections[sections.length - 1].toLowerCase();
}

export function getFileContentType(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        // https://developer.mozilla.org/en-US/docs/Web/API/FileReader/readAsDataURL
        // readAsDataURL returns a string formatted as such "data:*/*;base64," where */* represents the content type
        // for example, "data:application/pdf;base64,"
        // so we want to extract everything between ":" and ";" to get that value
        const contentType = reader.result.substring(reader.result.indexOf(':') + 1, reader.result.lastIndexOf(';'));
        if (contentType) {
          resolve(contentType);
        }
      }
      resolve('');
    };
    reader.onerror = (error) => reject(error);
  });
}

export async function validateFileType(
  file: File,
  acceptedFileTypes: string[],
  acceptedFileExtensions?: string[]
): Promise<boolean> {
  let fileType = file.type;
  if (!fileType) {
    fileType = await getFileContentType(file);
  }
  const acceptedFileType = acceptedFileTypes.some((type) => {
    if (type.includes('/*')) {
      const generalMimeType = type.split('/')[0];
      const fileMimeType = fileType.split('/')[0];

      return generalMimeType === fileMimeType;
    }

    return type === fileType;
  });

  const acceptedFileExtension = acceptedFileExtensions?.some((ext) => file.name.endsWith(ext));

  if (!acceptedFileType && !acceptedFileExtension) return false;

  const magicNumbers = (await import('./magic-numbers.json')).default;
  type IMagicNumbers = typeof magicNumbers;

  const fileExt = extractFileType(file.name);
  const magicNumberAttributes = (magicNumbers as IMagicNumbers)[fileExt as keyof IMagicNumbers];
  const bytes = await getFileFirstMBAsHex(file);

  if (!magicNumberAttributes || !bytes) {
    // some file types dont have magic numbers. If this is one of those, then we are not going to include magic number in the validation
    return true;
  }

  const validMagicNumber = magicNumberAttributes.signs.some((sign: string) => {
    const [offset, allowedMagicNumber] = sign.split(',');
    const offsetAsNumber = Number(offset || 0);
    const magicNumber = bytes
      .slice(offsetAsNumber, offsetAsNumber + 4)
      .join('')
      .toUpperCase();
    if (!magicNumber) return false;

    if (allowedMagicNumber.length < magicNumber.length) {
      return allowedMagicNumber.includes(magicNumber.slice(0, allowedMagicNumber.length));
    }
    return allowedMagicNumber.includes(magicNumber);
  });

  return validMagicNumber;
}
